import React, { useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
    fetchTicketsSettingsForQueue,
    setTicketsSettingsForQueue,
} from 'flow/actions/tickets-action';
import Page from 'flow/components/Setting/Page';
import Divider from 'flow/components/ui-components/Divider';
import SettingSelectableItem from 'flow/components/ui-components/SettingSelectableItem';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectQueueEditable } from 'flow/selectors/queue-selectors';
import { selectTicketCreationSetting } from 'flow/selectors/QueueTicketsSettingsSelectors';
import { selectQueueIsEligibleForTicketsConfiguration } from 'flow/selectors/TicketsSelectors';
import { QueueKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TICKETS_SETTINGS_PROPERTY_NAMES } from 'flow/utils/ticket-utils';

type Setting = 'NEVER' | 'ON_ALL_CALLS' | 'ON_MISSED_CALLS';

const PbxTicketsSettingsCreation = () => {
    const { i18next } = useAppContext();
    const { entityKey: queueEntityKey } = useParams<{ entityKey: QueueKey }>();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const queueIsEligibleForTicketsConfiguration = useAppSelector((state) =>
        selectQueueIsEligibleForTicketsConfiguration(state, queueEntityKey),
    );

    const userCanEditQueue = useAppSelector((state) =>
        selectQueueEditable(state, queueEntityKey),
    );

    const userCanActivateTicketsOnQueue =
        queueIsEligibleForTicketsConfiguration && userCanEditQueue;

    const creationSetting = useAppSelector((state) =>
        selectTicketCreationSetting(state, queueEntityKey),
    );

    const [isPerformingApiRequest, setIsPerformingApiRequest] = useState(false);

    const handleChange = (value: Setting) => {
        setIsPerformingApiRequest(true);
        dispatch(fetchTicketsSettingsForQueue(queueEntityKey)).finally(() => {
            dispatch(
                setTicketsSettingsForQueue(
                    queueEntityKey,
                    TICKETS_SETTINGS_PROPERTY_NAMES.AUTO_CREATION,
                    value,
                ),
            ).finally(() => {
                setIsPerformingApiRequest(false);
            });
        });
    };

    return (
        <Page
            header={i18next.t('ticket.auto.create.tickets.on.incoming.calls')}
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes['pbx.queues.entityKey.settings.tickets'],
                        {
                            entityKey: queueEntityKey,
                        },
                    ),
                )
            }
        >
            <>
                <Divider />
                <SettingSelectableItem
                    key={'NEVER'}
                    value={'NEVER'}
                    onClick={handleChange}
                    selected={creationSetting}
                    isDisabled={
                        !userCanActivateTicketsOnQueue || isPerformingApiRequest
                    }
                >
                    {i18next.t('ticket.settings.queue.create.tickets.on.never')}
                </SettingSelectableItem>
                <SettingSelectableItem
                    key={'ON_ALL_CALLS'}
                    value={'ON_ALL_CALLS'}
                    onClick={handleChange}
                    selected={creationSetting}
                    isDisabled={
                        !userCanActivateTicketsOnQueue || isPerformingApiRequest
                    }
                >
                    {i18next.t(
                        'ticket.settings.queue.create.tickets.on.allcalls',
                    )}
                </SettingSelectableItem>
                <SettingSelectableItem
                    key={'ON_MISSED_CALLS'}
                    value={'ON_MISSED_CALLS'}
                    onClick={handleChange}
                    selected={creationSetting}
                    isDisabled={
                        !userCanActivateTicketsOnQueue || isPerformingApiRequest
                    }
                >
                    {i18next.t(
                        'ticket.settings.queue.create.tickets.on.missedcalls',
                    )}
                </SettingSelectableItem>
                <Divider />
            </>
        </Page>
    );
};

export default PbxTicketsSettingsCreation;
